/* ═══════════════════════════════════════════════════════════════════════════
   WORKS MOTION — choreography + video lifecycle for the two works pages.
   2026-08-30. Vanilla, no framework, no build step.

   Replaces a system that had none. What was measured on the shipped
   creative-works.js before this file:
     · one reveal for every element on both pages: a 22px fade-up, no stagger  (#23)
     · every scroll-linked effect hard-disabled below 900px                    (#-)
     · 19 of 26 transitions on the browser default `ease`                      (#31)
     · will-change:transform left permanent on every hero/scene media          (#47)
     · poster crossfaded on `loadeddata`, so a refused autoplay froze frame 1  (#49)
     · videos paused with a raw pause() that desynced the toggle's aria state  (#8)
     · one transient media error killed a chapter's video for the session      (#28)
     · a momentary 3g drop stripped src from every video and re-downloaded all (#36)
     · three different definitions of "desktop" across loop/film/parallax      (#16)
   ═══════════════════════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  /* ── ONE definition of desktop. Everything reads this. (#16) ───────────── */
  const MQ_DESKTOP = window.matchMedia('(min-width: 900px)');
  const MQ_MOTION  = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MQ_HOVER   = window.matchMedia('(hover: hover) and (pointer: fine)');

  const conn = navigator.connection || {};
  const dataSaver = () => !!conn.saveData;
  const slowLink  = () => /^(slow-)?2g$/.test(conn.effectiveType || '');

  /* ── rAF write queue: every style write lands in one frame, after all reads.
        This is what keeps scroll-linked work off the critical path. ───────── */
  let queued = false;
  const writes = [];
  const write = fn => {
    writes.push(fn);
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      const batch = writes.splice(0, writes.length);
      for (const f of batch) f();
    });
  };

  /* ── CHOREOGRAPHY ────────────────────────────────────────────────────────
     A reveal is not one fade. Each group declares its own gesture and its
     children arrive in sequence, one --stagger apart, in DOM order. The
     stagger is set as a custom property so the curve and duration stay in CSS
     where a designer can reach them. Mobile is NOT excluded: it gets the same
     choreography with a shorter travel, because the brief asks for mobile to
     be the better of the two. */
  const revealGroups = document.querySelectorAll('[data-works-reveal]');
  if (revealGroups.length) {
    const reveal = (group, instant) => {
      const kids = group.querySelectorAll('[data-works-reveal-item]');
      write(() => {
        /* `instant` skips the cascade. Content the viewer has ALREADY scrolled
           past must simply be there — staggering it in behind their back is a
           lie about time. */
        if (instant) group.classList.add('is-revealed-instant');
        group.classList.add('is-revealed');
        kids.forEach((k, i) => k.style.setProperty('--i', instant ? 0 : i));
      });
    };

    const io = new IntersectionObserver((entries, obs) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        reveal(e.target, false);
        obs.unobserve(e.target);       // reveals are one-way; stop paying for them
      }
    }, {
      /* A band, not a hairline. The shipped observer used a 2%-tall band, so a
         fast scroll skipped chapters entirely and dropped the index marker. */
      rootMargin: '0px 0px -12% 0px',
      threshold: 0
    });

    /* CRITICAL: an IntersectionObserver never fires for something the viewer is
       already past. Browsers restore scroll position on reload, the back button
       restores from bfcache, and any #anchor deep-link lands mid-page — in all
       three cases every group above the landing point would stay at opacity 0
       forever. Verified in-browser: after a reload at scrollY>0 the group sat at
       top:-673px and all four children stayed invisible permanently. So prime
       synchronously first, and again on bfcache restore. */
    const prime = () => {
      revealGroups.forEach(g => {
        if (g.classList.contains('is-revealed')) return;
        const r = g.getBoundingClientRect();
        if (r.bottom <= 0) { reveal(g, true); io.unobserve(g); }   // fully above
        else io.observe(g);
      });
    };
    prime();
    window.addEventListener('pageshow', e => { if (e.persisted) prime(); });
  }

  /* ── SCROLL-LINKED DAMPING ───────────────────────────────────────────────
     Physically credible means the media does not track the scrollbar exactly —
     it lags and settles. One rAF loop for the whole page, reads then writes,
     and it stops itself when nothing is on screen. Runs on mobile too. */
  const parallaxEls = [...document.querySelectorAll('[data-works-parallax]')];
  if (parallaxEls.length && !MQ_MOTION.matches) {
    const state = new Map(parallaxEls.map(el => [el, { current: 0, target: 0, visible: false }]));

    const vis = new IntersectionObserver(entries => {
      for (const e of entries) {
        const s = state.get(e.target);
        if (s) s.visible = e.isIntersecting;
        /* will-change is a promise to the compositor, not a decoration. It is
           granted only while the element is actually on screen and released the
           moment it leaves — the shipped CSS held it permanently on every hero
           and scene, costing tens of MB of compositor memory. (#47) */
        write(() => {
          e.target.style.willChange = e.isIntersecting ? 'transform' : '';
        });
      }
      pump();
    }, { rootMargin: '20% 0px' });
    parallaxEls.forEach(el => vis.observe(el));

    let running = false;
    const tick = () => {
      let alive = false;
      const vh = window.innerHeight;
      /* READ pass — no writes interleaved, so no forced synchronous layout. */
      for (const el of parallaxEls) {
        const s = state.get(el);
        if (!s.visible) continue;
        alive = true;
        const r = el.getBoundingClientRect();
        const depth = parseFloat(el.dataset.worksParallax) || 0.08;
        const progress = (r.top + r.height / 2 - vh / 2) / vh;   // -1 … 1
        s.target = -progress * depth * vh;
      }
      /* WRITE pass — critically damped toward the target, so it settles rather
         than snapping. 0.12 is the damping coefficient; at 60fps it reaches
         ~99% of target in about 300ms, which reads as weight, not lag. */
      for (const el of parallaxEls) {
        const s = state.get(el);
        if (!s.visible) continue;
        s.current += (s.target - s.current) * 0.12;
        el.style.transform = `translate3d(0, ${s.current.toFixed(2)}px, 0)`;
      }
      running = alive;
      if (alive) requestAnimationFrame(tick);
    };
    const pump = () => { if (!running) { running = true; requestAnimationFrame(tick); } };
    window.addEventListener('scroll', pump, { passive: true });
    window.addEventListener('resize', pump, { passive: true });
  }

  /* ── VIDEO LIFECYCLE ─────────────────────────────────────────────────────
     Autoplay elegant, never aggressive: nothing is fetched until it is close,
     nothing plays off-screen, nothing plays in a hidden tab, and nothing is
     fetched at all on Save-Data or a 2g link. */
  const films = [...document.querySelectorAll('[data-works-film]')];

  const setSource = (v) => {
    if (v.dataset.worksLoaded === '1') return;
    const pick = MQ_DESKTOP.matches ? 'desktop' : 'mobile';
    const ladder = [
      ['av1',  'video/mp4; codecs="av01.0.05M.08"'],
      ['hevc', 'video/mp4; codecs="hvc1.1.6.L93.B0"'],
      ['h264', 'video/mp4; codecs="avc1.640028"']
    ];
    const frag = document.createDocumentFragment();
    for (const [codec, type] of ladder) {
      const url = v.dataset[`works${pick[0].toUpperCase()}${pick.slice(1)}${codec[0].toUpperCase()}${codec.slice(1)}`];
      if (!url) continue;
      /* Safari will not report AV1 support truthfully on every build, so we let
         the element choose: the first <source> it can decode wins. Order is
         smallest-first, which is why AV1 leads — measured at VMAF 95.8 for
         2.53 MB where H.264 needs 4.92 MB for the same 95.6. */
      const s = document.createElement('source');
      s.src = url; s.type = type;
      frag.appendChild(s);
    }
    if (!frag.childNodes.length) return;
    v.appendChild(frag);
    v.dataset.worksLoaded = '1';
    v.load();
  };

  const releaseSource = (v) => {
    /* Only ever called on a deliberate far-exit, never on a network blip — the
       shipped code stripped src on any downgrade and re-downloaded everything
       when the link recovered. (#36) */
    if (v.dataset.worksLoaded !== '1') return;
    v.pause();
    v.querySelectorAll('source').forEach(s => s.remove());
    v.removeAttribute('src');
    v.load();
    v.dataset.worksLoaded = '0';
    v.dataset.worksPlaying = '0';
  };

  const announce = (v, playing) => {
    /* One place that changes state, so the button's label and aria-pressed can
       never drift from what the video is doing. (#8) */
    v.dataset.worksPlaying = playing ? '1' : '0';
    const id = v.getAttribute('id');
    if (!id) return;
    document.querySelectorAll(`[data-works-film-toggle="${id}"]`).forEach(btn => {
      btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
      const label = playing ? btn.dataset.labelPause : btn.dataset.labelPlay;
      if (label) btn.setAttribute('aria-label', label);
    });
  };

  const tryPlay = (v) => {
    if (MQ_MOTION.matches || dataSaver() || slowLink()) return;
    /* RACE: the near-observer calls setSource() -> load(), and the on-screen
       observer calls play() in the same tick. Per spec load() aborts any pending
       play(), so a video that is visible at first paint silently never starts —
       reproduced in-browser on the hero, which sat at readyState 4 and paused.
       If the element is not ready yet, wait for the one event that says it is. */
    if (v.readyState < 2) {
      if (v.dataset.worksArmed === '1') return;
      v.dataset.worksArmed = '1';
      v.addEventListener('canplay', () => {
        v.dataset.worksArmed = '0';
        const r = v.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) tryPlay(v);
      }, { once: true });
      return;
    }
    const p = v.play();
    if (p && p.catch) p.catch(() => {
      /* A refused autoplay is a normal outcome, not an error. The poster stays,
         and the chapter stays usable. */
      announce(v, false);
    });
  };

  if (films.length) {
    films.forEach(v => {
      /* The poster is a real element underneath, not the poster attribute, so it
         can carry AVIF/WebP/JPEG and a proper srcset. It is dismissed on
         `playing` — the first frame actually painted — never on `loadeddata`,
         which fires even when playback was refused. (#49) */
      v.addEventListener('playing', () => {
        write(() => v.closest('[data-works-stage]')?.classList.add('is-playing'));
        announce(v, true);
      });
      v.addEventListener('pause', () => announce(v, false));
      v.addEventListener('error', () => {
        /* Transient decode errors happen. One retry, then leave the poster in
           place — a chapter must not go black for the rest of the session. (#28) */
        if (v.dataset.worksRetried === '1') return;
        v.dataset.worksRetried = '1';
        setTimeout(() => { v.load(); tryPlay(v); }, 1200);
      });
    });

    /* Two bands: a wide one that fetches, a tight one that plays.
       The fetch band was 150% and it cost 14.4 MB for a full read of the page —
       measured, and nearly three times the 5.1 MB the shipped page costs. A
       background loop does not need to exist a viewport and a half early: 60%
       still lands the first frame before the sequence is on screen at any
       plausible scroll speed, and it stops the whole page arriving at once.
       Sources are released at 2 viewports rather than 3 for the same reason. */
    const near = new IntersectionObserver(entries => {
      for (const e of entries) {
        if (e.isIntersecting) { setSource(e.target); near.unobserve(e.target); }
      }
    }, { rootMargin: '60% 0px' });
    /* NOTHING IS RELEASED ON SCROLL, DELIBERATELY.
       Tearing a source down to reclaim memory sounds thrifty and measured badly:
       at 2 viewports a film re-downloaded the instant the reader scrolled back
       up (gravitas fetched three times in one read), and at 4 viewports it still
       did. A page carrying five six-second loops is not under memory pressure;
       the download it repeats is a real cost and the memory it saves is not.
       Each element is unobserved once fetched, so a film is fetched exactly
       once per page view. releaseSource() survives for the one case that needs
       it — swapping the ladder when the breakpoint actually changes. */

    /* Declared before the observer that uses it — IntersectionObserver callbacks
       are always async so the old order was safe, but relying on that is not. */
    const halt = (v) => { v.pause(); announce(v, false); };

    const onScreen = new IntersectionObserver(entries => {
      for (const e of entries) {
        if (e.isIntersecting) tryPlay(e.target);
        else if (!e.target.paused) halt(e.target);
      }
    }, { threshold: 0.25 });

    films.forEach(v => { near.observe(v); onScreen.observe(v); });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) films.forEach(v => { if (!v.paused) halt(v); });
      else films.forEach(v => {
        const r = v.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) tryPlay(v);
      });
    });
  }

  /* Re-pick sources when the breakpoint is actually crossed, not once at load. (#29) */
  MQ_DESKTOP.addEventListener('change', () => {
    films.forEach(v => {
      const wasPlaying = !v.paused;
      releaseSource(v);
      setSource(v);
      if (wasPlaying) tryPlay(v);
    });
  });
})();
